import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Check } from "lucide-react";

export default function FinalCTA() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", brand: "", message: "" });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4000);
    setForm({ name: "", email: "", brand: "", message: "" });
  };

  return (
    <section
      id="contact"
      data-testid="final-cta-section"
      className="relative py-16 md:py-20 px-6 md:px-10 surface-1"
    >
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="relative overflow-hidden rounded-[28px] bg-[#F8FAFC] dark:bg-white/[0.04] border border-blue-100 dark:border-white/10 shadow-[0_30px_80px_-50px_rgba(30,58,138,0.35)]"
        >
          <div
            className="pointer-events-none absolute -top-40 -right-24 w-[500px] h-[500px] rounded-full blur-3xl opacity-60 dark:opacity-40"
            style={{
              background:
                "radial-gradient(circle, rgba(37,99,235,0.22) 0%, transparent 70%)",
            }}
          />
          <div
            className="pointer-events-none absolute -bottom-40 -left-24 w-[500px] h-[500px] rounded-full blur-3xl opacity-50 dark:opacity-30"
            style={{
              background:
                "radial-gradient(circle, rgba(59,130,246,0.18) 0%, transparent 70%)",
            }}
          />

          <div className="relative grid lg:grid-cols-[1fr_1fr]">
            {/* Left */}
            <div className="p-8 md:p-14 lg:p-16">
              <div className="inline-flex items-center gap-2 rounded-full bg-white dark:bg-white/10 border border-blue-100 dark:border-white/15 px-4 py-1.5 mb-8">
                <Sparkles className="w-3.5 h-3.5 text-[#2563EB]" />
                <span className="text-xs tracking-[0.2em] uppercase text-[#2563EB] dark:text-blue-200 font-semibold">
                  Strategy Call · 30 min · Free
                </span>
              </div>

              <h2
                data-testid="final-cta-headline"
                className="font-heading font-bold text-4xl sm:text-5xl lg:text-[56px] xl:text-6xl tracking-tight leading-[1.03] text-slate-900 dark:text-white"
              >
                Let's Make People <br />
                <span className="font-serif-italic font-medium gradient-text">Talk About Your Brand.</span>
              </h2>

              <p className="mt-6 text-base md:text-lg text-slate-600 dark:text-slate-300 max-w-md leading-relaxed">
                Tell us your brand, your goal, your region — we'll come back
                with a creator-led campaign blueprint within 48 hours.
              </p>

              <ul className="mt-8 space-y-3 text-slate-700 dark:text-slate-200">
                {[
                  "Regional cultural fluency across 4 languages",
                  "1000+ vetted creators on tap",
                  "Campaign blueprint in 48 hours",
                ].map((t) => (
                  <li key={t} className="flex items-center gap-3">
                    <span className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-500/20 text-[#2563EB] dark:text-blue-300 flex items-center justify-center">
                      <Check className="w-3 h-3" />
                    </span>
                    <span className="text-sm md:text-[15px]">{t}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right — form */}
            <div className="relative lg:border-l border-blue-100 dark:border-white/10">
              <form
                onSubmit={onSubmit}
                data-testid="cta-form"
                className="relative bg-white dark:bg-[#0f2250]/80 backdrop-blur-sm p-8 md:p-12 lg:p-14 h-full flex flex-col"
              >
                <div className="text-[11px] tracking-[0.25em] uppercase text-[#2563EB] font-semibold">
                  Start Your Campaign
                </div>
                <h3 className="mt-2 font-heading font-bold text-2xl md:text-3xl text-slate-900 dark:text-white">
                  Book Your Strategy Call
                </h3>

                <div className="mt-8 grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="cta-name" className="text-[12px] font-medium text-slate-600 dark:text-slate-300">
                      Name
                    </label>
                    <input
                      id="cta-name" name="name" type="text" required
                      value={form.name} onChange={onChange}
                      placeholder="Your name" data-testid="cta-input-name"
                      className="mt-1.5 w-full rounded-xl bg-[#F8FAFC] dark:bg-white/5 border border-slate-200 dark:border-white/10 px-4 py-3 text-[15px] text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:border-[#2563EB] focus:bg-white dark:focus:bg-white/[0.07] transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="cta-email" className="text-[12px] font-medium text-slate-600 dark:text-slate-300">
                      Email
                    </label>
                    <input
                      id="cta-email" name="email" type="email" required
                      value={form.email} onChange={onChange}
                      placeholder="you@brand.com" data-testid="cta-input-email"
                      className="mt-1.5 w-full rounded-xl bg-[#F8FAFC] dark:bg-white/5 border border-slate-200 dark:border-white/10 px-4 py-3 text-[15px] text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:border-[#2563EB] focus:bg-white dark:focus:bg-white/[0.07] transition-colors"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label htmlFor="cta-brand" className="text-[12px] font-medium text-slate-600 dark:text-slate-300">Brand</label>
                  <input
                    id="cta-brand" name="brand" type="text" required
                    value={form.brand} onChange={onChange}
                    placeholder="Company / Brand name" data-testid="cta-input-brand"
                    className="mt-1.5 w-full rounded-xl bg-[#F8FAFC] dark:bg-white/5 border border-slate-200 dark:border-white/10 px-4 py-3 text-[15px] text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:border-[#2563EB] focus:bg-white dark:focus:bg-white/[0.07] transition-colors"
                  />
                </div>
                <div className="mt-4">
                  <label htmlFor="cta-message" className="text-[12px] font-medium text-slate-600 dark:text-slate-300">Message</label>
                  <textarea
                    id="cta-message" name="message" rows={4}
                    value={form.message} onChange={onChange}
                    placeholder="Tell us about your goal, budget range, and target region."
                    data-testid="cta-input-message"
                    className="mt-1.5 w-full rounded-xl bg-[#F8FAFC] dark:bg-white/5 border border-slate-200 dark:border-white/10 px-4 py-3 text-[15px] text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:border-[#2563EB] focus:bg-white dark:focus:bg-white/[0.07] transition-colors resize-none"
                  />
                </div>

                <button
                  type="submit"
                  data-testid="cta-submit"
                  className="mt-6 group inline-flex items-center justify-center gap-2 rounded-full bg-[#2563EB] text-white px-7 py-4 text-[15px] font-semibold hover:bg-[#1E3A8A] transition-colors shadow-[0_18px_40px_-15px_rgba(37,99,235,0.55)]"
                >
                  Book a Strategy Call
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </button>

                {submitted && (
                  <div
                    data-testid="cta-success"
                    className="mt-4 rounded-xl bg-blue-50 dark:bg-blue-500/15 border border-blue-100 dark:border-blue-400/25 px-4 py-3 text-sm text-blue-800 dark:text-blue-200"
                  >
                    Thanks — we'll be in touch within 24 hours.
                  </div>
                )}
                <p className="mt-4 text-[12px] text-slate-500 dark:text-slate-400">
                  By submitting, you agree to be contacted by Affix regarding
                  your campaign inquiry.
                </p>
              </form>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
